import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, message, Space, Modal } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { api } from '../lib/api';
import { auth } from '../lib/auth';

const { Text, Paragraph } = Typography;

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [resendForm] = Form.useForm();

  const resolveReturnUrl = useCallback(async (returnUrl: string) => {
    if (!returnUrl.startsWith('/organizations/')) {
      return returnUrl;
    }
    try {
      const orgData = await api.organizations.list();
      const orgs = orgData.organizations || [];
      const hasAdminAccess = orgs.some((org: { role?: string }) =>
        org.role === 'OWNER' || org.role === 'ADMIN'
      );
      return hasAdminAccess ? returnUrl : '/apps';
    } catch {
      return '/apps';
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const navigateToReturnUrl = async (returnUrl: string) => {
      const resolvedReturnUrl = await resolveReturnUrl(returnUrl);
      if (resolvedReturnUrl.startsWith('/')) {
        navigate(resolvedReturnUrl);
        return;
      }
      try {
        const target = new URL(resolvedReturnUrl);
        window.location.assign(target.toString());
      } catch {
        navigate('/');
      }
    };
    const checkSession = async () => {
      try {
        const user = await api.auth.getSession();
        if (!mounted) return;
        if (user && typeof user === 'object') {
          auth.setAuthenticated();
          const returnUrl = searchParams.get('return') || '/';
          await navigateToReturnUrl(returnUrl);
        } else {
          auth.clear();
        }
      } catch {
        auth.clear();
      }
    };
    checkSession();
    return () => {
      mounted = false;
    };
  }, [navigate, resolveReturnUrl, searchParams]);

  useEffect(() => {
    const verified = searchParams.get('verified');
    const error = searchParams.get('error');
    if (!verified && !error) return;
    // Avoid double notifications in React.StrictMode
    const handledFlag = 'annea_portal_verification_notice';
    if (sessionStorage.getItem(handledFlag)) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('verified');
      nextParams.delete('error');
      const nextQuery = nextParams.toString();
      navigate(`/login${nextQuery ? `?${nextQuery}` : ''}`, { replace: true });
      return;
    }
    sessionStorage.setItem(handledFlag, '1');

    if (verified === '1' || verified === 'true') {
      api.apps
        .config()
        .then((cfg) => {
          const trialDays = Number(cfg?.trialDays) || 14;
          message.success(`Your account is verified. Your ${trialDays}-day trial for eligible subscription apps will be activated on first login.`);
        })
        .catch(() => {
          message.success('Your account is verified. Your trial for eligible subscription apps will be activated on first login.');
        });
    } else if (error) {
      const errorMap: Record<string, string> = {
        token_expired: 'Verification link expired. Please request a new one.',
        invalid_token: 'Invalid verification link. Please request a new one.',
        user_not_found: 'Account not found for this verification link.',
        unauthorized: 'Verification link is not valid for this account.',
      };
      message.error(errorMap[error] || 'Email verification failed.');
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('verified');
    nextParams.delete('error');
    const nextQuery = nextParams.toString();
    navigate(`/login${nextQuery ? `?${nextQuery}` : ''}`, { replace: true });
  }, [navigate, searchParams]);

  const onFinish = async (values: { email: string; password: string }) => {
    try {
      setLoading(true);
      await api.auth.signIn(values.email, values.password);
      const trialProvision = await api.user.ensureTrials().catch(() => null);
      if (Number(trialProvision?.createdCount || 0) > 0) {
        sessionStorage.setItem('annea_portal_show_welcome', '1');
      }
      auth.setAuthenticated();
      message.success('Login successful!');

      const returnUrl = searchParams.get('return') || '/';
      const resolvedReturnUrl = await resolveReturnUrl(returnUrl);
      if (resolvedReturnUrl.startsWith('/')) {
        navigate(resolvedReturnUrl);
        return;
      }
      try {
        const target = new URL(resolvedReturnUrl);
        window.location.assign(target.toString());
      } catch {
        navigate('/');
      }
    } catch (error: any) {
      message.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      const values = await resendForm.validateFields();
      setResendLoading(true);
      await api.auth.sendVerificationEmail(values.email);
      message.success('If the email exists, a verification link was sent.');
      setResendOpen(false);
      resendForm.resetFields();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.message || 'Failed to send verification email');
    } finally {
      setResendLoading(false);
    }
  };

  const primaryColor = '#f46524';
  const cardStyle = {
    width: 420,
    maxWidth: '100%',
    borderRadius: 12,
    border: `1px solid ${primaryColor}`,
    boxShadow: '0 12px 35px rgba(244, 101, 36, 0.25)',
    background: '#fffdfa',
    overflow: 'hidden',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: '#f5f5f5',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 24,
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'stretch',
          width: '100%',
          maxWidth: 900,
        }}
      >
        <Card
          title="Welcome to Annea Apps Portal"
          style={cardStyle}
          headStyle={{
            color: primaryColor,
            fontWeight: 600,
            textAlign: 'center',
            background: '#fffdfa',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Paragraph style={{ marginBottom: 0 }}>
              Annea Apps Portal provides web applications for validating, analyzing, 
              and visualizing aeronautical data. This portal provides access to tools from 
              the <b>Annea Geometry Validation Suite</b> and <b>Annea AIXM Suite</b>, with additional 
              applications being added regularly.
            </Paragraph>
            <h3>Authentication Required</h3>
            <Paragraph style={{ marginBottom: 0 }}>The application you're trying to access requires authentication. 
              Please <b>sign in</b> or <Link to="/signup"><b>create a free account</b></Link> to continue.</Paragraph>
            <Paragraph style={{ marginBottom: 0 }}>All paid applications include a <b>free trial period</b> with full functionality - test the tools with your real data with no commitment required.</Paragraph>
            <Paragraph style={{ marginBottom: 0 }}><b>New to Annea?</b> Learn more about our products and capabilities at <a href="https://annea.sk/#products" style={{ color: primaryColor }}>annea.sk/#products</a>.</Paragraph>
          </Space>
        </Card>
        <Card
          title="Annea Portal Sign In"
          style={cardStyle}
          headStyle={{
            color: primaryColor,
            fontWeight: 600,
            textAlign: 'center',
            background: '#fffdfa',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">Sign in to your account</Text>
            </div>

            <Form
              name="login"
              onFinish={onFinish}
              autoComplete="off"
              layout="vertical"
              requiredMark={false}
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'email', message: 'Enter a valid email' },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="name@example.com"
                  autoComplete="email"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Password"
                  autoComplete="current-password"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                >
                  Sign In
                </Button>
              </Form.Item>
            </Form>

            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Link to="/forgot-password">
                <Button type="link" block>
                  Forgot password?
                </Button>
              </Link>
              <Button type="link" block onClick={() => setResendOpen(true)}>
                Resend verification email
              </Button>
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary">Don't have an account? </Text>
                <Link to="/signup">Sign up</Link>
              </div>
            </Space>
          </Space>
        </Card>
      </div>

      <Modal
        title="Resend verification email"
        open={resendOpen}
        onOk={handleResend}
        onCancel={() => setResendOpen(false)}
        okText="Send"
        confirmLoading={resendLoading}
        destroyOnClose
      >
        <Form form={resendForm} layout="vertical" requiredMark={false}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Required' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input placeholder="name@example.com" autoComplete="email" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
